import React from 'react';
import { useTranslation } from 'react-i18next';
import { S, Icon, BtnPrimary, formatDate } from '../regureStyles';
import useRateTablesFunctions, { PGMA_AGE_BAND_ROWS, PGMA_WAITING_PERIODS } from './useRateTablesFunctions';

const inputStyle = {
  width: 90,
  padding: '6px 9px',
  background: '#fff',
  border: `1px solid ${S.borderLight}`,
  borderRadius: 6,
  fontSize: 12,
  color: S.textPrimary,
  outline: 'none',
  textAlign: 'right',
  boxSizing: 'border-box',
  fontVariantNumeric: 'tabular-nums',
};

const constInputStyle = {
  ...inputStyle,
  width: 130,
  textAlign: 'left',
};

const labelStyle = { fontSize: 11, fontWeight: 600, color: S.textSecondary, textTransform: 'uppercase', letterSpacing: 0.6 };

const RateGrid = ({ coverageLabel, field, grid, updateCell, t }) => (
  <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, overflow: 'hidden' }}>
    <div style={{ padding: '10px 14px', background: S.bgCard, borderBottom: `1px solid ${S.borderLight}`, fontSize: 12, fontWeight: 700, color: S.textPrimary }}>
      {coverageLabel}
    </div>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead>
        <tr>
          <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', borderBottom: `1px solid ${S.borderLight}` }}>
            {t('rateTables.ageBand')}
          </th>
          {PGMA_WAITING_PERIODS.map(wp => (
            <th key={wp} style={{ padding: '10px 14px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', borderBottom: `1px solid ${S.borderLight}` }}>
              {t('rateTables.daysHeader', { days: wp })}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {PGMA_AGE_BAND_ROWS.map(band => (
          <tr key={band.key}>
            <td style={{ padding: '8px 14px', fontSize: 13, color: S.textPrimary, fontWeight: 600 }}>
              {t(band.labelKey)}
            </td>
            {PGMA_WAITING_PERIODS.map(wp => {
              const v = grid?.[band.key]?.[wp]?.[field];
              return (
                <td key={wp} style={{ padding: '6px 10px', textAlign: 'right' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <input
                      type='number'
                      step='0.001'
                      min='0'
                      value={v ?? ''}
                      onChange={e => updateCell(band.key, wp, field, e.target.value)}
                      style={inputStyle}
                    />
                    <span style={{ fontSize: 12, color: S.textMuted }}>%</span>
                  </div>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ConstantsForm = ({ constants, updateConstant, t }) => {
  const fields = [
    { key: 'maxSalary',         label: t('rateTables.constMaxSalary'),         suffix: 'CHF',                    step: '1000' },
    { key: 'coveragePercent',   label: t('rateTables.constCoveragePercent'),   suffix: '%',                      step: '1' },
    { key: 'maxBenefitDays',    label: t('rateTables.constMaxBenefitDays'),    suffix: t('offers.common.daysUnit'), step: '1' },
    { key: 'maxBenefitDaysAVS', label: t('rateTables.constMaxBenefitDaysAVS'), suffix: t('offers.common.daysUnit'), step: '1' },
    { key: 'minimumPremium',    label: t('rateTables.constMinimumPremium'),    suffix: 'CHF',                    step: '1' },
    { key: 'maxEligibleAge',    label: t('rateTables.constMaxEligibleAge'),    suffix: t('rateTables.unitYears'), step: '1' },
    { key: 'avsAgeMen',         label: t('rateTables.constAvsAgeMen'),         suffix: t('rateTables.unitYears'), step: '1' },
    { key: 'avsAgeWomen',       label: t('rateTables.constAvsAgeWomen'),       suffix: t('rateTables.unitYears'), step: '1' },
  ];
  return (
    <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, padding: 18 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: S.textPrimary, marginBottom: 12 }}>{t('rateTables.productConstants')}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
        {fields.map(f => (
          <div key={f.key}>
            <label style={labelStyle}>{f.label}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <input
                type='number'
                step={f.step}
                value={constants[f.key] ?? ''}
                onChange={e => updateConstant(f.key, e.target.value)}
                style={constInputStyle}
              />
              <span style={{ fontSize: 12, color: S.textMuted }}>{f.suffix}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PgmaRatesEditor = () => {
  const { t } = useTranslation();
  const {
    table,
    grid, constants,
    updateCell, updateConstant,
    loading, saving, dirty, error, toast,
    save, reload,
  } = useRateTablesFunctions();

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18 }}>
        <div>
          {table?.effectiveDate && (
            <div style={{ fontSize: 11, color: S.textMuted }}>
              {t('rateTables.effective', { date: formatDate(table.effectiveDate) })}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {dirty && <span style={{ fontSize: 12, color: S.warning, fontWeight: 600 }}>{t('rateTables.unsavedChanges')}</span>}
          <button
            onClick={reload}
            disabled={loading || saving}
            style={{
              padding: '8px 14px', background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 8,
              fontSize: 13, fontWeight: 600, color: S.textSecondary, cursor: 'pointer',
              opacity: (loading || saving) ? 0.6 : 1,
            }}
          >
            {t('rateTables.reset')}
          </button>
          <BtnPrimary onClick={save} disabled={!dirty || saving} style={{ opacity: (!dirty || saving) ? 0.6 : 1 }}>
            <Icon name='check' color='#fff' size={15} /> {saving ? t('rateTables.saving') : t('rateTables.saveChanges')}
          </BtnPrimary>
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b', marginBottom: 14 }}>
          {error}
        </div>
      )}
      {toast && (
        <div style={{ padding: '10px 14px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, fontSize: 13, color: '#166534', marginBottom: 14 }}>
          {toast}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: S.textMuted, fontSize: 13 }}>{t('rateTables.loadingPgma')}</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 18, marginBottom: 20 }}>
            <RateGrid coverageLabel={t('rateTables.coverageMaladie')}         field='maladieRate'          grid={grid} updateCell={updateCell} t={t} />
            <RateGrid coverageLabel={t('rateTables.coverageMaladieAccident')} field='maladieAccidentRate' grid={grid} updateCell={updateCell} t={t} />
          </div>
          <ConstantsForm constants={constants} updateConstant={updateConstant} t={t} />
          <div style={{ marginTop: 14, fontSize: 11, color: S.textMuted }}>
            {t('rateTables.rateChangesNote')}
          </div>
        </>
      )}
    </>
  );
};

export default PgmaRatesEditor;
